import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GET_CHILD_ACTIVITY_QUERY } from 'src/graphql/entities/activity-feed/acitivity-feed.operations';
import { ActivityFeed } from './use-activity-feed';

export type ChildActivityFeedParams = {
  partnerId: number;
  /** `actorUid` of the activity whose direct children are wanted. */
  parentActorUid: string;
};

export type ChildActivityFeedInput = {
  params: ChildActivityFeedParams;
};

export interface GetChildActivityQuery {
  getChildActivityFeed: { records: ActivityFeed[] | null };
}

/**
 * Direct children of a single activity — the activities it caused.
 *
 * Every nested level comes through here, the first one included: the feed itself is flat, so nothing
 * is known about what a run caused until it is expanded. `getChildActivityFeed` returns a single
 * level, so it is one request per run the user actually opens.
 *
 * Apollo caches per `parentActorUid`, so collapsing and re-expanding does not refetch. Mount this
 * hook only once a run is open — mounting is the only gate on the request, and a feed full of
 * expandable rows would otherwise fire one per row.
 */
export const useActivityChildren = (parentActorUid: string, partnerId: number) => {
  const variables = useMemo<ChildActivityFeedInput>(
    () => ({ params: { partnerId, parentActorUid } }),
    [parentActorUid, partnerId],
  );

  const { data, loading, error } = useQuery<GetChildActivityQuery, ChildActivityFeedInput>(
    GET_CHILD_ACTIVITY_QUERY,
    { variables },
  );

  return {
    records: data?.getChildActivityFeed.records ?? undefined,
    loading,
    error,
  };
};
